import { useCallback, useEffect, useState } from 'react';

import {
  loadServerPreferences,
  saveServerPreferences,
  type PreferenceSync,
} from './preferences-api';

export type ProfilePreferences = {
  firstName: string;
  familyName: string;
  phone: string;
  bio: string;
  pictureUrl: string;
  timezone: string;
};

const PROFILE_STORAGE_PREFIX = 'quality.vet:profile:';

function preferenceKey(userKey: string): string {
  return `${PROFILE_STORAGE_PREFIX}${encodeURIComponent(userKey)}`;
}

function readPreferences(userKey: string, defaults: ProfilePreferences): ProfilePreferences {
  try {
    const saved = JSON.parse(window.localStorage.getItem(preferenceKey(userKey)) ?? '{}') as {
      firstName?: unknown;
      familyName?: unknown;
      fullName?: unknown;
      phone?: unknown;
      bio?: unknown;
      pictureUrl?: unknown;
      timezone?: unknown;
    };

    // Backward compatibility: split fullName into firstName/familyName if the new fields are absent
    let firstName = defaults.firstName;
    let familyName = defaults.familyName;
    if (typeof saved.firstName === 'string') {
      firstName = saved.firstName;
    } else if (typeof saved.fullName === 'string') {
      const parts = saved.fullName.trim().split(/\s+/);
      firstName = parts[0] ?? defaults.firstName;
      familyName = parts.slice(1).join(' ') || defaults.familyName;
    }
    if (typeof saved.familyName === 'string') {
      familyName = saved.familyName;
    }

    return {
      firstName,
      familyName,
      phone: typeof saved.phone === 'string' ? saved.phone : defaults.phone,
      bio: typeof saved.bio === 'string' ? saved.bio : defaults.bio,
      pictureUrl: typeof saved.pictureUrl === 'string' ? saved.pictureUrl : defaults.pictureUrl,
      timezone: typeof saved.timezone === 'string' ? saved.timezone : defaults.timezone,
    };
  } catch {
    return defaults;
  }
}

export function useProfilePreferences(
  userKey: string | undefined,
  defaults: ProfilePreferences,
  sync?: PreferenceSync,
) {
  const [profile, setProfileState] = useState(defaults);

  useEffect(() => {
    setProfileState(userKey ? readPreferences(userKey, defaults) : defaults);
  }, [defaults.firstName, defaults.familyName, defaults.pictureUrl, defaults.timezone, userKey]);

  useEffect(() => {
    if (!userKey || !sync?.accessToken) return;
    let active = true;
    void loadServerPreferences(sync).then((saved) => {
      if (!active || !saved) return;

      // Backward compatibility: handle server still returning fullName
      let firstName = saved.firstName ?? '';
      let familyName = saved.familyName ?? '';
      if (!firstName && !familyName && saved.fullName) {
        const parts = saved.fullName.trim().split(/\s+/);
        firstName = parts[0] ?? '';
        familyName = parts.slice(1).join(' ');
      }

      const next: ProfilePreferences = {
        firstName,
        familyName,
        phone: saved.phone ?? '',
        bio: saved.bio ?? '',
        pictureUrl: saved.pictureUrl,
        timezone: saved.timezone,
      };
      setProfileState(next);
      try {
        window.localStorage.setItem(preferenceKey(userKey), JSON.stringify(next));
      } catch {
        // Server preferences remain authoritative when browser storage is unavailable.
      }
    });
    return () => {
      active = false;
    };
  }, [sync?.accessToken, sync?.apiUrl, userKey]);

  const setProfile = useCallback(
    (nextProfile: ProfilePreferences) => {
      setProfileState(nextProfile);
      if (!userKey) return;
      try {
        window.localStorage.setItem(preferenceKey(userKey), JSON.stringify(nextProfile));
      } catch {
        // The in-memory values still apply when browser storage is unavailable.
      }
      if (sync) {
        const fullName = `${nextProfile.firstName} ${nextProfile.familyName}`.trim();
        void saveServerPreferences(sync, { ...nextProfile, fullName }).catch(() => undefined);
      }
    },
    [sync, userKey],
  );

  return { profile, setProfile };
}
